import { useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';
import { HrPageShell } from './components/HrPageShell';
import { EmptyState } from './components/hr-ui';
import { useEmployees } from './hr-store';
import { useLeaveRequestsQuery } from '@/client/api/hr/people';
import { cn } from '@/lib/utils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function eachDay(startIso: string, endIso: string): string[] {
  const days: string[] = [];
  const start = new Date(startIso.slice(0, 10));
  const end = new Date(endIso.slice(0, 10));
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

const TimeOffCalendarPage = () => {
  const employees = useEmployees();
  const [monthOffset, setMonthOffset] = useState(0);
  const base = useMemo(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth() + monthOffset, 1));
  }, [monthOffset]);
  const from = base.toISOString().slice(0, 10);
  const to = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
  const requestsQ = useLeaveRequestsQuery({ from, to });
  const shown = useMemo(
    () => (requestsQ.data ?? []).filter((r) => r.status === 'approved' || r.status === 'pending'),
    [requestsQ.data],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, { employeeId: string; typeName: string; pending: boolean }[]>();
    for (const r of shown) {
      for (const day of eachDay(r.startDate, r.endDate)) {
        map.set(day, [...(map.get(day) ?? []), { employeeId: r.employeeId, typeName: r.typeName, pending: r.status === 'pending' }]);
      }
    }
    return map;
  }, [shown]);

  const employeeName = (id: string) => employees.find((e) => e.id === id)?.name ?? 'Unknown';

  const monthCells = useMemo(() => {
    const year = base.getUTCFullYear();
    const month = base.getUTCMonth();
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const leadingBlanks = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const days = Array.from({ length: daysInMonth }, (_, i) => new Date(Date.UTC(year, month, i + 1)).toISOString().slice(0, 10));
    return [...Array.from({ length: leadingBlanks }, () => null), ...days];
  }, [base]);

  const monthLabel = base.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <HrPageShell
      mock={false}
      title="Time off calendar"
      description="Who's out, at a glance. Approved time off is solid; requests still waiting for a decision are dashed."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Time Off' }, { label: 'Calendar' }]}
      icon={Calendar}
      actions={
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setMonthOffset((m) => m - 1)} className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50">
            ←
          </button>
          <span className="text-sm font-medium text-gray-700">{monthLabel}</span>
          <button type="button" onClick={() => setMonthOffset((m) => m + 1)} className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50">
            →
          </button>
        </div>
      }
    >
      {requestsQ.isError ? (
        <EmptyState label={`Could not load time off: ${(requestsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : requestsQ.isLoading ? (
        <EmptyState label="Loading time off…" />
      ) : (
        <div>
          <div className="mb-1 grid grid-cols-7 gap-2">
            {WEEKDAYS.map((w) => (
              <p key={w} className="text-center text-[11px] font-medium uppercase tracking-wide text-gray-400">
                {w}
              </p>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {monthCells.map((day, i) => {
              if (day === null) return <div key={`blank_${i}`} />;
              const entries = byDay.get(day) ?? [];
              return (
                <div key={day} className={cn('min-h-[92px] rounded-lg border bg-white p-2', entries.length > 0 ? 'border-amber-200' : 'border-gray-100')}>
                  <p className="mb-1 text-[11px] font-medium text-gray-400">{new Date(day).getUTCDate()}</p>
                  <div className="space-y-1">
                    {entries.map((entry, idx) => (
                      <span
                        key={idx}
                        title={`${employeeName(entry.employeeId)} · ${entry.typeName}${entry.pending ? ' (pending)' : ''}`}
                        className={cn(
                          'block truncate rounded px-1.5 py-0.5 text-[10px] font-medium',
                          entry.pending ? 'border border-dashed border-amber-300 text-amber-700' : 'bg-amber-50 text-amber-800',
                        )}
                      >
                        {employeeName(entry.employeeId)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </HrPageShell>
  );
};

export default TimeOffCalendarPage;
